import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AuthGoogleButton from '../components/auth/AuthGoogleButton';
import { useGoogleClientId } from '../components/AsyncGoogleOAuthProvider';
import { googleSignInErrorMessage } from '../utils/googleOAuthOriginHelp';
import {
  FiUser,
  FiMail,
  FiLock,
  FiEye,
  FiEyeOff,
  FiArrowRight,
  FiHome,
  FiArrowLeft,
  FiCheckCircle,
} from 'react-icons/fi';
import { AuthContext } from '../context/AuthContext';
import { useThemeClasses } from '../hooks/useThemeClasses';
import { apiUrl } from '../config';
import { readApiJson } from '../utils/apiResponse';
import { sanitizeText, sanitizeEmail, validators } from '../utils/sanitize';
import { filterProductPlans } from '../utils/planUtils';
import { resolvePlanId } from '../utils/authSession';
import {
  loginWithGoogleCredential,
  storeGoogleOnboardingSession,
  loadGoogleOnboardingSession,
  clearGoogleOnboardingSession,
} from '../utils/googleAuth';
import SignupStepIndicator from '../components/auth/SignupStepIndicator';
import PlanSelectionCards from '../components/auth/PlanSelectionCards';
import BusinessFiscalFields from '../components/auth/BusinessFiscalFields';
import { FormSection, FormField, FormAlert, formInputClass } from '../components/form';
import CountrySubdivisionSelect from '../components/form/CountrySubdivisionSelect';
import TransactionalEmailHint from '../components/auth/TransactionalEmailHint';
import AuthSplitShell from '../components/auth/AuthSplitShell';
import {
  captureReferralFromSearch,
  getStoredReferralCode,
  clearStoredReferralCode,
} from '../utils/referral';
import { resolvePostPaymentDestination } from '../utils/onboarding';
import AcceptTermsField from '../components/legal/AcceptTermsField';

const STEP_LABELS = ['Datos de acceso', 'Tipo de cuenta', 'Elegí tu plan'];
const STEP_LABELS_GOOGLE = ['Tu acceso', 'Tipo de cuenta', 'Elegí tu plan'];
const SIGNUP_AWAITING_KEY = 'contsuite_signup_awaiting';
const SIGNUP_CONTINUE_KEY = 'contsuite_signup_continue_url';

const Register = () => {
  const tc = useThemeClasses();
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const googleClientId = useGoogleClientId();

  const [step, setStep] = useState(1);
  const [fromGoogle, setFromGoogle] = useState(false);
  const [googleSession, setGoogleSession] = useState(null);
  const [userType, setUserType] = useState('individual');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const handleBillingCycleChange = useCallback((cycle) => {
    setBillingCycle(cycle === 'annual' ? 'annual' : 'monthly');
  }, []);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');

  const [companyName, setCompanyName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [cuit, setCuit] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [signupCountry, setSignupCountry] = useState('Argentina');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [regimenFiscal, setRegimenFiscal] = useState('');
  const [categoriaMonotributo, setCategoriaMonotributo] = useState('');
  const [actividad, setActividad] = useState('servicios');

  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [referralCode, setReferralCode] = useState('');
  const [referralHint, setReferralHint] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [checkoutPending, setCheckoutPending] = useState(false);
  const [awaitingMpReturn, setAwaitingMpReturn] = useState(false);
  const [awaitingMpPayment, setAwaitingMpPayment] = useState(false);
  const [mpWaitMessage, setMpWaitMessage] = useState('');
  const [betaProPromo, setBetaProPromo] = useState(null);
  const [mpPayerEmail, setMpPayerEmail] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailVerificationToken, setEmailVerificationToken] = useState('');
  const [emailVerifyCode, setEmailVerifyCode] = useState('');
  const [sendingEmailCode, setSendingEmailCode] = useState(false);
  const [verifyingEmailCode, setVerifyingEmailCode] = useState(false);
  const [emailCodeHint, setEmailCodeHint] = useState('');

  useEffect(() => {
    loadPlans();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(apiUrl('/api/signup/beta-pro'));
        const data = await readApiJson(res);
        if (!cancelled && res.ok) setBetaProPromo(data);
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(SIGNUP_AWAITING_KEY) === '1') {
        setAwaitingMpReturn(true);
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    const code = captureReferralFromSearch(location.search) || getStoredReferralCode();
    if (!code) return;
    setReferralCode(code);
    fetch(apiUrl(`/api/referrals/validate/${encodeURIComponent(code)}`))
      .then(async (res) => (res.ok ? readApiJson(res) : null))
      .then((data) => {
        if (data?.referrerName) {
          setReferralHint(`${data.referrerName} te invitó · +${data.bonusDocuments} registros de bienvenida`);
        }
      })
      .catch(() => {});
  }, [location.search]);

  // Retomar registro Google (desde Login o tras refrescar la página)
  useEffect(() => {
    const fromNav = location.state?.googleAuth;
    const fromStorage = loadGoogleOnboardingSession();
    const data = fromNav || fromStorage;
    if (!data?.token && !data?.googleSignupToken) return;

    const session = {
      user: data.user,
      token: data.token || null,
      refreshToken: data.refreshToken || null,
      googleSignupToken: data.googleSignupToken || null,
    };

    setGoogleSession(session);
    storeGoogleOnboardingSession(session);
    if (data.user?.name) setName(data.user.name);
    if (data.user?.email) setEmail(data.user.email);
    setFromGoogle(true);
    setStep(1);

    if (fromNav) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const clearSignupWaitStorage = () => {
    try {
      sessionStorage.removeItem('contsuite_signup_preapproval');
      sessionStorage.removeItem('contsuite_signup_external_reference');
      sessionStorage.removeItem('contsuite_signup_email');
      sessionStorage.removeItem(SIGNUP_AWAITING_KEY);
      sessionStorage.removeItem(SIGNUP_CONTINUE_KEY);
    } catch {
      /* ignore */
    }
  };

  const finishSignupLogin = useCallback((user, token, refreshToken) => {
    clearSignupWaitStorage();
    setAwaitingMpReturn(false);
    setAwaitingMpPayment(false);
    setMpWaitMessage('');
    login(user, token, refreshToken);
    navigate(resolvePostPaymentDestination(user, { newSignup: true }), { replace: true });
  }, [login, navigate]);

  /** MP en otra pestaña: poll /signup/session hasta que el cobro cree la sesión (sin ir a Login). */
  useEffect(() => {
    if (!awaitingMpPayment && !awaitingMpReturn) return undefined;

    let cancelled = false;

    const tryCompleteSignup = async () => {
      let preapprovalId = '';
      let signupEmail = '';
      try {
        preapprovalId = sessionStorage.getItem('contsuite_signup_preapproval') || '';
        signupEmail = sessionStorage.getItem('contsuite_signup_email') || '';
      } catch {
        /* ignore */
      }
      if (!preapprovalId && !signupEmail) return;

      const params = new URLSearchParams();
      if (preapprovalId) params.set('preapprovalId', preapprovalId);
      if (signupEmail) params.set('email', signupEmail);

      try {
        const res = await fetch(apiUrl(`/api/signup/session?${params.toString()}`));
        const data = await readApiJson(res);
        if (cancelled) return;
        if (data.token && data.user) {
          finishSignupLogin(data.user, data.token, data.refreshToken);
        }
      } catch {
        /* ignore */
      }
    };

    tryCompleteSignup();
    const intervalId = window.setInterval(tryCompleteSignup, 2500);
    const onFocus = () => {
      tryCompleteSignup();
    };
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
    };
  }, [awaitingMpPayment, awaitingMpReturn, finishSignupLogin]);

  const loadPlans = async () => {
    try {
      setLoadingPlans(true);
      const response = await fetch(apiUrl('/api/plans'));
      if (response.ok) {
        const data = await readApiJson(response);
        setPlans(filterProductPlans(data));
      } else {
        setError('Error al cargar los planes. Por favor, recargá la página.');
      }
    } catch (err) {
      setError(
        err.message === 'Failed to fetch' || err.name === 'TypeError'
          ? 'No hay conexión con el API. Iniciá el backend: cd backend && npm start (puerto 5000).'
          : err.message || 'Error de conexión. Verificá que el backend esté en marcha (puerto 5000).'
      );
    } finally {
      setLoadingPlans(false);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  const validateStep1Fields = ({ requireTerms = false } = {}) => {
    const sanitizedName = sanitizeText(name);
    const sanitizedEmail = sanitizeEmail(email);

    if (!sanitizedName.trim()) return 'El nombre es requerido';
    if (!sanitizedEmail) return 'El email es requerido';

    const emailValidation = validators.email(sanitizedEmail);
    if (!emailValidation.valid) return emailValidation.error;

    if (!password) return 'La contraseña es requerida';
    const passwordValidation = validators.password(password);
    if (!passwordValidation.valid) return passwordValidation.error;

    if (password !== repeatPassword) return 'Las contraseñas no coinciden';
    if (requireTerms && !acceptTerms) return 'Debés aceptar los términos y la política de privacidad';
    return null;
  };

  const validateStep1 = () => validateStep1Fields({ requireTerms: true });

  const validateGoogleStep1 = () => {
    if (!sanitizeText(name).trim()) return 'El nombre es requerido';
    if (!acceptTerms) return 'Debés aceptar los términos y la política de privacidad';
    if (password || repeatPassword) {
      if (!password) return 'Ingresá la contraseña o dejá ambos campos vacíos';
      const passwordValidation = validators.password(password);
      if (!passwordValidation.valid) return passwordValidation.error;
      if (password !== repeatPassword) return 'Las contraseñas no coinciden';
    }
    return null;
  };

  const cuitDigits = (value) => sanitizeText(value).replace(/\D/g, '');

  const validateStep2 = () => {
    if (!sanitizeText(companyName).trim()) return 'El nombre de la empresa es requerido';
    const cuitNorm = cuitDigits(cuit);
    if (!cuitNorm) return 'El CUIT es requerido (Monotributo o RI)';
    if (cuitNorm.length !== 11) return 'El CUIT debe tener 11 dígitos';
    if (!sanitizeText(address).trim()) return 'La dirección es requerida';
    if (!regimenFiscal) return 'Indicá si sos Monotributo o Responsable inscripto';
    if (regimenFiscal === 'monotributo' && !categoriaMonotributo) {
      return 'Elegí la categoría de Monotributo';
    }
    if (userType === 'company') {
      if (!sanitizeText(legalName).trim()) return 'La razón social es requerida';
    }
    return null;
  };

  const validateStep3 = () => {
    if (!selectedPlan) return 'Debes seleccionar un plan';
    const mp = sanitizeEmail(mpPayerEmail);
    if (mpPayerEmail.trim() && !mp) return 'Ingresá un email de Mercado Pago válido';
    return null;
  };

  const resetEmailVerification = () => {
    setEmailVerified(false);
    setEmailVerificationToken('');
    setEmailVerifyCode('');
    setEmailCodeHint('');
  };

  const handleSendEmailVerificationCode = async () => {
    const stepError = validateStep1Fields();
    if (stepError) {
      setError(stepError);
      return;
    }
    const sanitizedEmail = sanitizeEmail(email);
    setError('');
    setSendingEmailCode(true);
    setEmailCodeHint('');
    try {
      const res = await fetch(apiUrl('/api/signup/email/send-code'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: sanitizedEmail }),
      });
      const data = await readApiJson(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo enviar el código');
      resetEmailVerification();
      setEmailCodeHint(data.message || 'Código enviado. Revisá tu casilla.');
    } catch (err) {
      setError(sanitizeText(err.message));
    } finally {
      setSendingEmailCode(false);
    }
  };

  const handleVerifyEmailCode = async () => {
    const sanitizedEmail = sanitizeEmail(email);
    const code = String(emailVerifyCode || '').replace(/\D/g, '');
    if (!code || code.length !== 6) {
      setError('Ingresá el código de 6 dígitos que te enviamos por email.');
      return;
    }
    setError('');
    setVerifyingEmailCode(true);
    try {
      const res = await fetch(apiUrl('/api/signup/email/verify-code'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: sanitizedEmail, code }),
      });
      const data = await readApiJson(res);
      if (!res.ok) throw new Error(data.message || 'Código incorrecto');
      setEmailVerificationToken(data.emailVerificationToken || '');
      setEmailVerified(true);
      setEmailCodeHint('Correo verificado. Podés continuar.');
      setSuccess('Email verificado correctamente.');
    } catch (err) {
      setError(sanitizeText(err.message));
    } finally {
      setVerifyingEmailCode(false);
    }
  };

  const nextStep = () => {
    let stepError = null;
    if (step === 1) stepError = fromGoogle ? validateGoogleStep1() : validateStep1();
    else if (step === 2) stepError = validateStep2();
    if (stepError) {
      setError(stepError);
      return;
    }
    if (step === 1 && !fromGoogle && !emailVerified) {
      setError('Verificá tu correo con el código que te enviamos antes de continuar.');
      return;
    }
    setError('');
    setSuccess('');
    setStep(step + 1);
  };

  const prevStep = () => {
    setStep(step - 1);
    setError('');
  };

  const buildSignupPreparePayload = () => {
    const mpEmail = sanitizeEmail(mpPayerEmail);
    const signupEmail = sanitizeEmail(email);
    return {
    planId: resolvePlanId(selectedPlan),
    billingCycle: billingCycle === 'annual' ? 'annual' : 'monthly',
    name: sanitizeText(name),
    userType: userType === 'company' ? 'company' : 'individual',
    ...(mpEmail && mpEmail !== signupEmail ? { mpPayerEmail: mpEmail } : {}),
    ...(referralCode ? { referralCode } : {}),
    ...(fromGoogle ? {} : {
      email: sanitizeEmail(email),
      password,
      emailVerificationToken,
    }),
    companyData: {
      razonSocial: sanitizeText(companyName),
      cuit: cuitDigits(cuit),
      domicilio: sanitizeText(address),
      pais: sanitizeText(signupCountry) || 'Argentina',
      provincia: sanitizeText(state),
      localidad: sanitizeText(city),
      email: sanitizeEmail(email),
      telefono: sanitizeText(phone),
    },
    fiscal: {
      regimenFiscal,
      categoriaMonotributo: regimenFiscal === 'monotributo' ? categoriaMonotributo : null,
      actividad,
    },
  };
  };

  const buildSignupContinueUrl = (checkout) => {
    const params = new URLSearchParams();
    params.set('source', 'signup');
    if (checkout.preapprovalId) params.set('preapproval_id', checkout.preapprovalId);
    if (checkout.externalReference) params.set('external_reference', checkout.externalReference);
    return `${window.location.origin}/pago/resultado?${params.toString()}`;
  };

  const continueSignupAfterMp = () => {
    try {
      const url = sessionStorage.getItem(SIGNUP_CONTINUE_KEY);
      if (url) {
        window.location.assign(url);
        return;
      }
    } catch {
      /* ignore */
    }
    navigate('/pago/resultado?source=signup');
  };

  const startSignupCheckout = async () => {
    const stored = loadGoogleOnboardingSession();
    const googleToken = googleSession?.googleSignupToken || stored?.googleSignupToken;

    if (fromGoogle && !googleToken) {
      throw new Error('Sesión de Google expirada. Volvé a registrarte con Google.');
    }

    const headers = { 'Content-Type': 'application/json' };
    if (fromGoogle && googleToken) {
      headers.Authorization = `Bearer ${googleToken}`;
    }

    const res = await fetch(apiUrl('/api/signup/prepare'), {
      method: 'POST',
      headers,
      body: JSON.stringify(buildSignupPreparePayload()),
    });
    const data = await readApiJson(res);
    if (!res.ok) {
      throw new Error(data.message || 'No se pudo iniciar el pago en Mercado Pago');
    }

    if ((data.alreadyActive || data.betaProGranted) && data.token && data.user) {
      clearStoredReferralCode();
      clearGoogleOnboardingSession();
      setCheckoutPending(false);
      if (data.message) setSuccess(data.message);
      finishSignupLogin(data.user, data.token, data.refreshToken);
      return;
    }

    clearStoredReferralCode();
    clearGoogleOnboardingSession();
    setCheckoutPending(false);

    if (!data.initPoint) {
      throw new Error('Mercado Pago no devolvió la URL de pago');
    }
    try {
      if (data.preapprovalId) {
        sessionStorage.setItem('contsuite_signup_preapproval', data.preapprovalId);
      }
      if (data.externalReference) {
        sessionStorage.setItem('contsuite_signup_external_reference', data.externalReference);
      }
      sessionStorage.setItem('contsuite_signup_email', sanitizeEmail(email));
      const continueUrl = buildSignupContinueUrl(data);
      sessionStorage.setItem(SIGNUP_CONTINUE_KEY, continueUrl);
      sessionStorage.setItem(SIGNUP_AWAITING_KEY, '1');
    } catch {
      /* ignore */
    }
    setAwaitingMpReturn(true);
    setAwaitingMpPayment(true);
    setMpWaitMessage(
      'Se abrió Mercado Pago en otra pestaña. Pagá ahí con el usuario de prueba (sandbox) o tu cuenta real (prod). '
      + 'Esta ventana te llevará al inicio sola en cuanto confirmemos el cobro — no hace falta abrir Login.'
    );

    const mpTab = window.open(data.initPoint, 'contsuite_mercadopago_checkout');
    if (!mpTab) {
      setMpWaitMessage(
        'El navegador bloqueó la pestaña nueva. Te llevamos a Mercado Pago; al volver, usá «Ya pagué — continuar registro».'
      );
      window.location.assign(data.initPoint);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    if (!acceptTerms) {
      setError('Debés aceptar los términos y la política de privacidad antes de continuar con Google');
      return;
    }
    setLoading(true);
    try {
      const data = await loginWithGoogleCredential(credentialResponse.credential);

      if (!data.needsOnboarding && !data.isNewUser) {
        login(data.user, data.token, data.refreshToken);
        navigate('/inicio');
        return;
      }

      if (data.token && data.user && !data.isNewUser) {
        login(data.user, data.token, data.refreshToken);
        navigate(data.needsOnboarding ? '/completar-perfil' : '/inicio');
        return;
      }

      // Usuario nuevo: NO loguear aún (GuestRoute redirige si hay sesión)
      const session = {
        user: data.user,
        token: data.token,
        refreshToken: data.refreshToken,
        googleSignupToken: data.googleSignupToken || null,
      };
      setGoogleSession(session);
      storeGoogleOnboardingSession(session);
      if (data.user?.name) setName(data.user.name);
      if (data.user?.email) setEmail(data.user.email);
      setFromGoogle(true);
      setStep(1);
    } catch (err) {
      setError(sanitizeText(err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const stepError = validateStep3();
    if (stepError) {
      setError(stepError);
      return;
    }

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await startSignupCheckout();
    } catch (err) {
      setCheckoutPending(true);
      setError(
        sanitizeText(err.message) ||
          'No pudimos abrir Mercado Pago. Revisá tu conexión o reintentá abajo (tu cuenta aún no se crea hasta confirmar el pago).'
      );
    } finally {
      setLoading(false);
    }
  };

  const inputWithIcon = (icon, inputProps, toggleProps) => (
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">{icon}</div>
      <input className={`${formInputClass(tc.inputClass)} pl-9 ${toggleProps ? 'pr-9' : ''}`} {...inputProps} />
      {toggleProps && (
        <button
          type="button"
          className={`absolute inset-y-0 right-0 pr-3 flex items-center ${tc.textMuted} hover:opacity-80`}
          {...toggleProps}
        />
      )}
    </div>
  );

  const renderGoogleStep1 = () => (
    <div className="space-y-3">
      <div
        className={`rounded-xl border p-3 flex items-center gap-3 ${
          tc.modoOscuro ? 'border-gray-700 bg-gray-900/60' : 'border-gray-200 bg-white'
        }`}
      >
        <div className="w-9 h-9 rounded-full bg-[#1F80FF]/10 flex items-center justify-center shrink-0">
          <FiCheckCircle className="w-4 h-4 text-[#1F80FF]" />
        </div>
        <div className="min-w-0">
          <p className={`text-xs font-semibold uppercase tracking-wide ${tc.textMuted}`}>Google verificado</p>
          <p className={`text-sm font-medium truncate ${tc.textPri}`}>{email}</p>
        </div>
      </div>

      <FormField label="Nombre completo" required labelClass={tc.labelClass}>
        {inputWithIcon(<FiUser className={`h-4 w-4 ${tc.textMuted}`} />, {
          type: 'text',
          value: name,
          onChange: (e) => setName(e.target.value),
          required: true,
          placeholder: 'Tu nombre completo',
        })}
      </FormField>

      <FormSection
        title="Contraseña (opcional)"
        description="Solo si querés entrar también con email y contraseña."
        dense
        section={tc.section}
        textPri={tc.textPri}
        textMuted={tc.textMuted}
      >
        <div className="space-y-2.5">
          <FormField label="Contraseña" labelClass={tc.labelClass}>
            {inputWithIcon(
              <FiLock className={`h-4 w-4 ${tc.textMuted}`} />,
              {
                type: showPassword ? 'text' : 'password',
                value: password,
                onChange: (e) => setPassword(e.target.value),
                placeholder: 'Opcional — mínimo 8 caracteres',
              },
              {
                onClick: () => setShowPassword((v) => !v),
                tabIndex: -1,
                'aria-label': showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña',
                children: showPassword ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />,
              }
            )}
          </FormField>

          {(password || repeatPassword) && (
            <FormField label="Confirmar contraseña" labelClass={tc.labelClass}>
              {inputWithIcon(
                <FiLock className={`h-4 w-4 ${tc.textMuted}`} />,
                {
                  type: showRepeatPassword ? 'text' : 'password',
                  value: repeatPassword,
                  onChange: (e) => setRepeatPassword(e.target.value),
                  placeholder: 'Repetí tu contraseña',
                },
                {
                  onClick: () => setShowRepeatPassword((v) => !v),
                  tabIndex: -1,
                  'aria-label': showRepeatPassword ? 'Ocultar contraseña' : 'Mostrar contraseña',
                  children: showRepeatPassword ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />,
                }
              )}
            </FormField>
          )}
        </div>
      </FormSection>

      <AcceptTermsField checked={acceptTerms} onChange={setAcceptTerms} tc={tc} />
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-3">
      <AuthGoogleButton
        className="w-full"
        onSuccess={handleGoogleSuccess}
        onError={(detail) => setError(googleSignInErrorMessage({ clientId: googleClientId, ...detail }))}
        modoOscuro={tc.modoOscuro}
        text="signup_with"
      />

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className={`w-full border-t ${tc.divide}`} />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className={`px-3 ${tc.modoOscuro ? 'bg-black' : 'bg-[#f5f5f7]'} ${tc.textMuted}`}>o con email</span>
        </div>
      </div>

      <div className="space-y-3">
        <FormField label="Nombre completo" required labelClass={tc.labelClass}>
          {inputWithIcon(<FiUser className={`h-4 w-4 ${tc.textMuted}`} />, {
            type: 'text',
            value: name,
            onChange: (e) => setName(e.target.value),
            required: true,
            placeholder: 'Tu nombre completo',
            autoFocus: true,
          })}
        </FormField>

        <FormField label="Correo electrónico" required labelClass={tc.labelClass}>
          {inputWithIcon(<FiMail className={`h-4 w-4 ${tc.textMuted}`} />, {
            type: 'email',
            value: email,
            onChange: (e) => {
              setEmail(e.target.value);
              resetEmailVerification();
            },
            required: true,
            placeholder: 'tu@email.com',
          })}
        </FormField>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField label="Contraseña" required labelClass={tc.labelClass}>
            {inputWithIcon(
              <FiLock className={`h-4 w-4 ${tc.textMuted}`} />,
              {
                type: showPassword ? 'text' : 'password',
                value: password,
                onChange: (e) => setPassword(e.target.value),
                required: true,
                placeholder: 'Mín. 8 caracteres',
                autoComplete: 'new-password',
              },
              {
                onClick: () => setShowPassword((v) => !v),
                tabIndex: -1,
                'aria-label': showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña',
                children: showPassword ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />,
              }
            )}
          </FormField>

          <FormField label="Confirmar" required labelClass={tc.labelClass}>
            {inputWithIcon(
              <FiLock className={`h-4 w-4 ${tc.textMuted}`} />,
              {
                type: showRepeatPassword ? 'text' : 'password',
                value: repeatPassword,
                onChange: (e) => setRepeatPassword(e.target.value),
                required: true,
                placeholder: 'Repetí la clave',
                autoComplete: 'new-password',
              },
              {
                onClick: () => setShowRepeatPassword((v) => !v),
                tabIndex: -1,
                'aria-label': showRepeatPassword ? 'Ocultar contraseña' : 'Mostrar contraseña',
                children: showRepeatPassword ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />,
              }
            )}
          </FormField>
        </div>

        <div
          className={`rounded-xl border px-3 py-2.5 space-y-2 ${
            emailVerified
              ? tc.modoOscuro ? 'border-emerald-800 bg-emerald-950/30' : 'border-emerald-200 bg-emerald-50/80'
              : tc.modoOscuro ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-gray-50/80'
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className={`text-[11px] leading-snug ${tc.textMuted}`}>
              {emailVerified ? 'Email verificado.' : 'Último paso: código de 6 dígitos a tu casilla.'}
            </p>
            {emailVerified && (
              <span className={`inline-flex items-center gap-1 text-[11px] font-semibold shrink-0 ${tc.modoOscuro ? 'text-emerald-400' : 'text-emerald-700'}`}>
                <FiCheckCircle className="w-3.5 h-3.5" />
                Listo
              </span>
            )}
          </div>
          {!emailVerified && (
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                disabled={sendingEmailCode}
                onClick={handleSendEmailVerificationCode}
                className={`shrink-0 px-3 py-2 rounded-lg text-xs font-semibold ${
                  tc.modoOscuro ? 'bg-gray-800 text-white' : 'bg-white border border-gray-200 text-gray-900'
                } disabled:opacity-50`}
              >
                {sendingEmailCode ? 'Enviando…' : 'Enviar código'}
              </button>
              <input
                className={`${formInputClass(tc.inputClass)} flex-1 min-w-0 py-2 text-sm`}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={emailVerifyCode}
                onChange={(e) => setEmailVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Código 6 dígitos"
                aria-label="Código del email"
              />
              <button
                type="button"
                disabled={verifyingEmailCode || emailVerifyCode.length !== 6}
                onClick={handleVerifyEmailCode}
                className="shrink-0 px-3 py-2 rounded-lg text-xs font-semibold bg-[#1F80FF] text-white disabled:opacity-50"
              >
                {verifyingEmailCode ? '…' : 'Confirmar'}
              </button>
            </div>
          )}
          {emailCodeHint && !emailVerified && (
            <p className={`text-[11px] ${tc.textMuted}`}>{emailCodeHint}</p>
          )}
          {emailCodeHint && !emailVerified && (
            <TransactionalEmailHint className="mt-1" />
          )}
        </div>
      </div>

      <AcceptTermsField checked={acceptTerms} onChange={setAcceptTerms} tc={tc} />
    </div>
  );

  const betaSignupLine =
    betaProPromo?.enabled && betaProPromo.remaining > 0
      ? `Beta: plan ${betaProPromo.planName || 'Profesional'} gratis${betaProPromo.freeDays ? ` (${betaProPromo.freeDays} días)` : ''} · sin Mercado Pago · quedan ${betaProPromo.remaining} cupos.`
      : '';

  const renderStep2 = () => (
    <FormSection
      title="Tipo de cuenta"
      description={betaSignupLine}
      dense
      tight
      section={tc.section}
      textPri={tc.textPri}
      textMuted={tc.textMuted}
      descriptionClassName="leading-snug"
    >
      <div className="grid grid-cols-2 gap-2 mb-2">
        {[
          { id: 'individual', icon: FiUser, title: 'Emprendedor' },
          { id: 'company', icon: FiHome, title: 'Negocio / estudio' },
        ].map(({ id, icon: Icon, title }) => (
          <button
            key={id}
            type="button"
            onClick={() => setUserType(id)}
            className={`p-2 rounded-xl border text-left transition-colors flex items-center gap-2 ${
              userType === id
                ? 'border-[#1F80FF] ring-1 ring-[#1F80FF]/20 bg-[#1F80FF]/5'
                : tc.modoOscuro
                  ? 'border-gray-700 hover:border-gray-600 bg-gray-900/80'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
            }`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                userType === id ? 'bg-[#1F80FF] text-white' : tc.modoOscuro ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'
              }`}
            >
              <Icon className="w-4 h-4" />
            </div>
            <span className={`font-semibold text-xs sm:text-sm leading-tight ${tc.textPri}`}>{title}</span>
          </button>
        ))}
      </div>

      <BusinessFiscalFields
        tc={tc}
        compact
        tight
        extras={userType === 'company'}
        values={{
          companyName,
          legalName,
          cuit,
          address,
          regimenFiscal,
          categoriaMonotributo,
          actividad,
        }}
        onChange={(field, value) => {
          if (field === 'companyName') setCompanyName(value);
          else if (field === 'legalName') setLegalName(value);
          else if (field === 'cuit') setCuit(cuitDigits(value));
          else if (field === 'address') setAddress(value);
          else if (field === 'regimenFiscal') {
            setRegimenFiscal(value);
            if (value !== 'monotributo') setCategoriaMonotributo('');
          } else if (field === 'categoriaMonotributo') setCategoriaMonotributo(value);
          else if (field === 'actividad') setActividad(value);
        }}
      />
      <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-x-2 gap-y-1.5">
        <CountrySubdivisionSelect
          signupOnly
          lockCountry
          defaultCountryCode="AR"
          countryName={signupCountry}
          provinceName={state}
          onCountryChange={(name) => setSignupCountry(name || 'Argentina')}
          onProvinceChange={(name) => setState(name)}
          showCity={false}
          provinceRequired={userType === 'company'}
          tc={tc}
          compact
          className="sm:col-span-2"
        />
        <FormField
          label="Ciudad / localidad"
          labelClass={tc.labelClass}
          className={userType === 'company' ? '' : 'sm:col-span-2'}
        >
          <input
            className={formInputClass(tc.inputClass, { compact: true })}
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Ciudad"
          />
        </FormField>
        {userType === 'company' && (
          <FormField label="Teléfono" labelClass={tc.labelClass}>
            <input
              className={formInputClass(tc.inputClass, { compact: true })}
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+54 11…"
            />
          </FormField>
        )}
      </div>
    </FormSection>
  );

  const retryMercadoPago = async () => {
    if (!checkoutPending) return;
    setError('');
    setLoading(true);
    try {
      await startSignupCheckout();
    } catch (err) {
      setError(sanitizeText(err.message) || 'No pudimos abrir Mercado Pago. Revisá el token en el servidor.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep3 = () => (
    <div className="space-y-4">
      <div className={`rounded-2xl border p-3 sm:p-4 ${tc.section}`}>
        <h3 className={`text-base sm:text-lg font-bold ${tc.textPri}`}>Elegí tu plan</h3>
        <p className={`mt-1 text-xs sm:text-sm ${tc.textMuted}`}>
          {betaProPromo?.enabled && betaProPromo.remaining > 0
            ? 'Los precios son de referencia. En esta etapa beta activás tu cuenta sin pagar (cupos limitados).'
            : fromGoogle
              ? 'Mercado Pago autoriza la suscripción. Al confirmar el pago entrás a Contsuite (también te enviamos un email).'
              : 'Mercado Pago cobra la suscripción. Al confirmar el pago activamos tu cuenta y te llevamos al inicio.'}
        </p>
        {!(betaProPromo?.enabled && betaProPromo.remaining > 0) && (
          <p className={`mt-2 text-xs ${tc.textSec}`}>
            Mercado Pago te pedirá iniciar sesión con el email de tu cuenta MP (abajo). En Contsuite entrás con el email de registro. Después del pago tocá «Ir al sitio del vendedor».
          </p>
        )}
      </div>

      {!(betaProPromo?.enabled && betaProPromo.remaining > 0) && (
        <FormField
          label="Email de Mercado Pago"
          labelClass={tc.labelClass}
          hint="Si pagás con otra cuenta distinta al email de Contsuite, escribila acá. Al pagar, usá esa misma cuenta en MP."
          textMuted={tc.textMuted}
        >
          <input
            type="email"
            value={mpPayerEmail}
            onChange={(e) => setMpPayerEmail(e.target.value)}
            placeholder={email ? `${sanitizeEmail(email)} (mismo que registro)` : 'cuenta@mercadopago.com'}
            className={formInputClass(tc.inputClass, { compact: true })}
            autoComplete="email"
          />
        </FormField>
      )}

      <PlanSelectionCards
        plans={plans}
        selectedPlan={selectedPlan}
        onSelect={setSelectedPlan}
        loading={loadingPlans}
        onRetry={loadPlans}
        modoOscuro={tc.modoOscuro}
        compact
        signup
        billingCycle={billingCycle}
        onBillingCycleChange={handleBillingCycleChange}
        betaSignupZeroPrice={Boolean(betaProPromo?.enabled && betaProPromo.remaining > 0)}
        betaFreeDays={betaProPromo?.freeDays}
        textPri={tc.textPri}
        textSec={tc.textSec}
        textMuted={tc.textMuted}
      />

      {checkoutPending && (
        <FormAlert type="warning" className="text-sm">
          No se abrió Mercado Pago. Reintentá abajo; todavía no se creó tu cuenta hasta confirmar el pago.
        </FormAlert>
      )}
    </div>
  );

  const btnRow = 'flex gap-2.5 sm:gap-3';
  const btnBase = 'flex-1 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 min-h-[44px]';

  const renderSignupFooter = () => {
    if (step === 1) {
      return (
        <button type="button" onClick={nextStep} className={`w-full ${btnBase} ${tc.btnPrimary}`}>
          Continuar
          <FiArrowRight className="w-4 h-4" />
        </button>
      );
    }
    if (step === 2) {
      return (
        <div className={btnRow}>
          <button type="button" onClick={prevStep} className={`${btnBase} border ${tc.btnOutline}`}>
            <FiArrowLeft className="w-4 h-4" />
            Anterior
          </button>
          <button type="button" onClick={nextStep} className={`${btnBase} ${tc.btnPrimary}`}>
            Continuar
            <FiArrowRight className="w-4 h-4" />
          </button>
        </div>
      );
    }
    return (
      <div className={`${btnRow} flex-col sm:flex-row`}>
        <button type="button" onClick={prevStep} disabled={loading} className={`${btnBase} border ${tc.btnOutline}`}>
          <FiArrowLeft className="w-4 h-4" />
          Anterior
        </button>
        {checkoutPending ? (
          <button type="button" onClick={retryMercadoPago} disabled={loading} className={`${btnBase} sm:flex-[1.4] disabled:opacity-50 ${tc.btnPrimary}`}>
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Abriendo Mercado Pago…
              </>
            ) : (
              <>
                Reintentar pago con Mercado Pago
                <FiArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        ) : (
          <button
            type="submit"
            form="register-form"
            disabled={loading || !selectedPlan}
            className={`${btnBase} sm:flex-[1.4] disabled:opacity-50 disabled:cursor-not-allowed ${tc.btnPrimary}`}
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Redirigiendo…
              </>
            ) : (
              <>
                {betaProPromo?.enabled && betaProPromo.remaining > 0
                  ? 'Crear cuenta gratis (beta)'
                  : fromGoogle
                    ? 'Ir a Mercado Pago'
                    : 'Crear cuenta e ir a Mercado Pago'}
                <FiArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        )}
      </div>
    );
  };

  const focusForm = step >= 1;

  return (
    <>
      {(awaitingMpPayment || awaitingMpReturn) && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 px-4">
          <div className={`w-full max-w-md rounded-2xl border p-6 text-center shadow-xl ${tc.box}`}>
            <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-[#1F80FF] border-t-transparent" />
            <h2 className={`text-lg font-bold ${tc.textPri}`}>Esperando Mercado Pago</h2>
            <p className={`mt-2 text-sm ${tc.textMuted}`}>
              {mpWaitMessage
                || 'Confirmamos tu pago y te llevamos al inicio automáticamente (sin ir a Login).'}
            </p>
            <button
              type="button"
              onClick={continueSignupAfterMp}
              className={`mt-4 w-full rounded-xl px-4 py-2.5 text-sm font-semibold ${tc.btnSecondary}`}
            >
              Ya pagué — continuar ahora
            </button>
          </div>
        </div>
      )}
    <AuthSplitShell
      focusForm={focusForm}
      hideBrand={focusForm}
      centerVertically={false}
      dense={step === 2}
      planPicker={step === 3}
      contentWidth={step === 3 ? 'plan' : step === 2 ? 'signup' : 'default'}
      footer={renderSignupFooter()}
    >
      <div className={step === 2 ? 'mb-2' : 'mb-3 sm:mb-4'}>
        <h2
          className={`font-extrabold tracking-tight ${tc.textPri} ${
            step === 2 ? 'text-lg sm:text-xl' : 'text-xl sm:text-2xl'
          }`}
        >
          {fromGoogle ? 'Completá tu registro' : 'Crear cuenta'}
        </h2>
        {step === 1 && (
          <p className={`mt-1 text-sm ${tc.textMuted}`}>
            {fromGoogle
              ? 'Google verificó tu email. Nombre y contraseña opcional.'
              : 'Datos de acceso y verificación de email.'}
          </p>
        )}
        {step === 2 && (
          <p className={`mt-0.5 text-xs ${tc.textMuted}`}>
            CUIT, dirección y régimen fiscal.
          </p>
        )}
        {step !== 2 && betaProPromo?.enabled && betaProPromo.remaining > 0 && (
          <p
            className={`mt-2 text-xs font-medium rounded-lg px-3 py-2 ${
              tc.modoOscuro ? 'bg-emerald-950/40 text-emerald-200 border border-emerald-800' : 'bg-emerald-50 text-emerald-900 border border-emerald-200'
            }`}
          >
            Beta Contsuite: los próximos {betaProPromo.remaining} registros reciben plan{' '}
            <strong>{betaProPromo.planName || 'Profesional'}</strong> gratis
            {betaProPromo.freeDays ? ` (${betaProPromo.freeDays} días)` : ''}, sin Mercado Pago.
          </p>
        )}
        {referralHint && (
          <p
            className={`mt-2 text-xs font-medium rounded-lg px-3 py-2 ${
              tc.modoOscuro ? 'bg-[#1F80FF]/15 text-[#9ec9ff]' : 'bg-[#1F80FF]/10 text-[#004AAD]'
            }`}
          >
            {referralHint}
          </p>
        )}
        {awaitingMpReturn && (
          <FormAlert type="info" className="mt-3 text-sm text-left">
            <p className="font-semibold mb-1">¿Ya pagaste en Mercado Pago?</p>
            <p className="mb-3">
              MP a veces no redirige solo. Podés tocar «Volver al sitio» en MP (te lleva a contsuite.com.ar)
              o terminá el registro acá en local con el botón de abajo.
            </p>
            <button
              type="button"
              onClick={continueSignupAfterMp}
              className={`w-full sm:w-auto px-4 py-2 rounded-lg font-semibold ${tc.btnPrimary}`}
            >
              Ya pagué — continuar registro
            </button>
          </FormAlert>
        )}
      </div>

      <SignupStepIndicator
        step={step}
        total={3}
        labels={fromGoogle ? STEP_LABELS_GOOGLE : STEP_LABELS}
        modoOscuro={tc.modoOscuro}
        compact={focusForm}
        tight={step === 2}
      />

      {(error || success) && (
        <div className="mb-2">
          {error && <FormAlert type="error" className="mb-0 text-sm py-2">{error}</FormAlert>}
          {success && !error && <FormAlert type="success" className="mb-0 text-sm py-2">{success}</FormAlert>}
        </div>
      )}

      <form id="register-form" onSubmit={handleSubmit}>
        {step === 1 && (fromGoogle ? renderGoogleStep1() : renderStep1())}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
      </form>

      {step === 1 && !fromGoogle && (
        <p className={`mt-4 text-center text-sm ${tc.textSec}`}>
          ¿Ya tenés cuenta?{' '}
          <Link to="/login" className="font-semibold text-[#1F80FF] hover:underline">
            Iniciá sesión
          </Link>
        </p>
      )}
    </AuthSplitShell>
    </>
  );
};

export default Register;
